import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { EntityType, useAppSelector } from '@frontend/common';
import { useLocale } from '@frontend/lang';
import { MqttBroker } from '@frontend/pub-sub';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAccountRepository } from '../account/account-repository';
import { buildCollectionMqttTopic, resolveListQueryAccountId } from '../mqtt/entity-mqtt-topic';
import { DefaultListValues, ListInfo, allSelect, fetch as func, initList, listStore, refresh, resetActiveFilters, select } from './list-slice';

interface Props<T> {
    dispatch: ThunkDispatch<any, any, Action>;
    fetch: (arg?: ApiQueryParams<DefaultQueryParams | string | number>) => Promise<PaginatedResponse<T>>;
    eventListener?: EntityType;
    defaultListValues?: DefaultListValues;
    listId?: string;
}

export function useListRepository<T>(props: Props<T>) {
    const [listId] = useState<string>(props.listId ?? crypto.randomUUID());
    const store = useAppSelector(useSelector, listStore);
    const list: ListInfo | undefined = store[listId];
    const { store: accountStore } = useAccountRepository();
    const { locale } = useLocale();
    const pageIndex = list?.pageIndex ?? props.defaultListValues?.pageIndex ?? 0;
    const pageSize = list?.pageSize ?? props.defaultListValues?.pageSize ?? 25;
    const sorting = list?.sorting ? list.sorting : props.defaultListValues?.sorting ? props.defaultListValues.sorting : [];
    const filter = list?.filter ? list.filter : props.defaultListValues?.filter ? props.defaultListValues.filter : {};
    const activeFilters = list ? (list.activeFilters ?? {}) : (props.defaultListValues?.activeFilters ?? {});
    const [refreshIndex, changeRefreshIndex] = useState<string>('');
    const listRef = useRef(list);
    listRef.current = list;
    const hasInitialisedList = useRef(false);

    const currentAccountRef = useRef(accountStore.currentAccount);

    useEffect(() => {
        if (hasInitialisedList.current) return;
        props.dispatch(initList({ listId, defaultListValues: props.defaultListValues ?? {} }));
        hasInitialisedList.current = true;
    }, [listId]);
    useEffect(() => {
        currentAccountRef.current = accountStore.currentAccount;
    }, [accountStore.currentAccount]);
    useEffect(() => {
        if (list && list.refresh) {
            refreshList();
            props.dispatch(refresh({ listId: listId, refresh: false }));
        }
    }, [list?.refresh]);

    const refreshList = () => {
        props.dispatch(
            func({
                key: listId,
                params: {
                    index: pageIndex.toString(),
                    size: pageSize.toString(),
                    account_id: resolveListQueryAccountId(accountStore.currentAccount, currentAccountRef.current),
                    ...(sorting.length > 0 && {
                        sort_field: sorting[0].id,
                        sort_direction: sorting[0].desc ? 'desc' : 'asc'
                    }),
                    ...filter,
                    ...activeFilters,
                    language: locale
                },
                func: props.fetch
            })
        );
    };

    const onSelect = (objId: string) => {
        props.dispatch(
            select({
                listId: listId,
                objId: objId
            })
        );
    };

    const onAllSelect = () => {
        props.dispatch(
            allSelect({
                listId: listId,
                index: pageIndex,
                size: pageSize
            })
        );
    };

    useEffect(() => {
        if (props.eventListener == undefined || !accountStore.currentAccount) return;
        const onEvent = (message: string, data: any) => {
            refreshList();
        };
        const topic = buildCollectionMqttTopic(accountStore.currentAccount, props.eventListener);
        MqttBroker.getInstance().subscribe(listId, topic, onEvent);
        return () => {
            MqttBroker.getInstance().unSubscribe(listId, topic);
        };
    }, [accountStore.currentAccount, refreshIndex]);

    useEffect(() => {
        // Wait for initList so the first fetch uses the list's real pageSize/sorting.
        if (!list || !accountStore.currentAccount || !locale) return;

        //Extra check for changes because react will call this function multiple times
        const newIndex = JSON.stringify({
            sorting: sorting,
            pageIndex: pageIndex,
            pageSize: pageSize,
            account: accountStore.currentAccount,
            locale,
            filters: filter,
            activeFilters: activeFilters
        });

        if (newIndex != refreshIndex) {
            refreshList();
            changeRefreshIndex(() => newIndex);
        }
    }, [list, sorting, pageIndex, pageSize, accountStore.currentAccount, locale, list?.filter, list?.activeFilters]);

    const data = useMemo(() => {
        if (list == undefined || list.results == null) return null;
        if (list.pages?.[pageIndex]) return list.pages[pageIndex] as T[];
        return list.results as T[];
    }, [store, pageIndex]);

    const totalCount = useMemo(() => {
        if (list == undefined || list.count == null) return 0;
        return list.count;
    }, [store]);

    const selected = useMemo(() => {
        if (list == undefined) return [];
        return list.selected as T[];
    }, [store]);

    const isAllSelected = useMemo(() => {
        if (list && list.results) {
            const relevantObjects = list.pages?.[pageIndex] ?? list.results;
            if (relevantObjects.length === 0) return false;
            return relevantObjects.every((obj) => list.selected!.find((o) => o.id == obj.id) != undefined);
        }
        return false;
    }, [store, list, pageIndex]);

    const error = useMemo(() => {
        if (list == undefined) return false;
        return list.error;
    }, [store]);

    const isLoading = useMemo(() => {
        if (list == undefined) return true;
        return list.isLoading;
    }, [store]);

    const isUpdating = useMemo(() => {
        if (list == undefined) return false;
        return list.isLoading && data !== null;
    }, [store, pageIndex]);

    useEffect(() => {
        return () => {
            if (listRef.current) {
                props.dispatch(resetActiveFilters({ listId }));
            }
        };
    }, []);

    return {
        listId,
        totalCount,
        data,
        selected,
        isAllSelected,
        error,
        isLoading,
        isUpdating,
        onSelect,
        onAllSelect
    };
}

export default useListRepository;
